import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';

export interface BusinessRegistrationRequest {
  businessName: string;
  businessType: string;
  taxId: string;
  businessAddress: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  industry: string;
  annualRevenue: number;
  numberOfEmployees: number;
  businessWebsite: string;
  username: string;
  password: string;
  businessDescription: string;
  dbaName: string;
  businessLicense: string;
  ein: string;
  businessPhone: string;
  faxNumber: string;
  businessHours: string;
  yearEstablished: string;
  businessCategory: string;
  hasMultipleLocations: boolean;
  parentBusinessId: string;
  complianceOfficerEmail: string;
  riskLevel: string;
}

export interface BusinessRegistrationState {
  isLoading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: BusinessRegistrationState = {
  isLoading: false,
  error: null,
  success: false,
};

export const registerBusiness = createAsyncThunk<
  void,
  BusinessRegistrationRequest,
  { state: RootState }
>(
  'businessRegistration/registerBusiness',
  async (registrationData, { rejectWithValue }) => {
    try {
      const response = await fetch('http://localhost:8080/business/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Business registration failed');
      }
      return;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Unknown error');
    }
  }
);

const businessRegistrationSlice = createSlice({
  name: 'businessRegistration',
  initialState,
  reducers: {
    clearBusinessRegistrationState(state) {
      state.isLoading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerBusiness.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(registerBusiness.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
      })
      .addCase(registerBusiness.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.success = false;
      });
  },
});

export const { clearBusinessRegistrationState } = businessRegistrationSlice.actions;
export default businessRegistrationSlice.reducer; 